import { useEffect, useState } from 'react';
import axios from 'axios';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Search, X, IndianRupee, TrendingUp, Receipt, Wallet, Plus, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtMoney = (n) =>
  '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const todayStr = () => format(new Date(), 'yyyy-MM-dd');

// Settlement rows carry the date the admin picked: show just the day (the
// time of day is meaningless for a back-dated entry). Others keep date+time.
const fmtTxnDate = (t) => {
  if (t.settlementDate) {
    return new Date(t.settlementDate).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  }
  return fmtDate(t.createdAt);
};

const fmtDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  return dt.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const statusColor = (status) => {
  switch (status) {
    case 'SUCCESS':
      return 'bg-green-100 text-green-700';
    case 'PENDING':
      return 'bg-amber-100 text-amber-700';
    case 'FAILED':
    case 'CANCELLED':
      return 'bg-red-100 text-red-700';
    case 'REFUNDED':
      return 'bg-blue-100 text-blue-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const StatCard = ({ icon: Icon, label, value, accent, info, hint }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-start gap-4">
    <div className={`p-2 rounded-lg ${accent}`}>
      <Icon size={20} />
    </div>
    <div className="min-w-0">
      <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
        {label}
        {info && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" aria-label={`What is ${label}?`} className="text-gray-400 hover:text-gray-700 normal-case">
                <Info size={13} />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs leading-snug normal-case tracking-normal">{info}</TooltipContent>
          </Tooltip>
        )}
      </p>
      <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
      {hint && <p className="text-[11px] text-gray-500 mt-1 leading-snug">{hint}</p>}
    </div>
  </div>
);

const TransactionsTab = ({ restaurantId }) => {
  const [data, setData] = useState({ summary: null, transactions: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  // Settlement form
  const [showSettleForm, setShowSettleForm] = useState(false);
  const [settle, setSettle] = useState({
    type: 'RESTAURANT_SETTLEMENT',
    amount: '',
    paymentMethod: 'CASH',
    settlementDate: todayStr(),
    note: ''
  });
  const [saving, setSaving] = useState(false);

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/restaurants/${restaurantId}/transactions`,
        { params: { limit: 200 } }
      );
      setData(res.data || { summary: null, transactions: [] });
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (restaurantId) loadTransactions();
    // eslint-disable-next-line
  }, [restaurantId]);

  const submitSettlement = async (e) => {
    e?.preventDefault();
    const amt = Number(settle.amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error('Enter a positive amount');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/restaurants/${restaurantId}/transactions`, {
        type: settle.type,
        amount: amt,
        paymentMethod: settle.paymentMethod || undefined,
        settlementDate: settle.settlementDate || undefined,
        note: settle.note || undefined
      });
      toast.success(
        settle.type === 'RESTAURANT_SETTLEMENT'
          ? 'Settlement recorded'
          : 'Earning recorded'
      );
      setSettle({
        type: 'RESTAURANT_SETTLEMENT',
        amount: '',
        paymentMethod: 'CASH',
        settlementDate: todayStr(),
        note: ''
      });
      setShowSettleForm(false);
      loadTransactions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record transaction');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-orange-600 border-t-transparent" />
      </div>
    );
  }

  const { summary, transactions } = data;

  // Backend only returns these two types — hardcode the filter options
  const TYPE_OPTIONS = ['RESTAURANT_EARNING', 'RESTAURANT_SETTLEMENT'];
  const statuses = Array.from(
    new Set(transactions.map((t) => t.status).filter(Boolean))
  );

  const q = search.trim().toLowerCase();
  const filtered = transactions.filter((t) => {
    if (statusFilter !== 'all' && t.status !== statusFilter) return false;
    if (typeFilter !== 'all' && t.type !== typeFilter) return false;
    if (q) {
      const hay = [t.orderNumber, t.orderID, t.id, t.paymentMethod]
        .filter(Boolean)
        .map((v) => String(v).toLowerCase());
      if (!hay.some((v) => v.includes(q))) return false;
    }
    return true;
  });

  return (
    <div className="space-y-6" data-testid="transactions-tab">
      {/* Summary cards */}
      <TooltipProvider delayDuration={100}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Receipt}
          label="Transactions"
          value={summary?.totalCount ?? 0}
          info="Number of ledger entries for this restaurant: one 'Earning' entry per delivered order (plus any manual earning) and one 'Settlement' entry per payout you recorded. It is not the number of orders."
          hint="Earning + settlement entries"
          accent="bg-blue-50 text-blue-700"
        />
        <StatCard
          icon={Wallet}
          label="Wallet Balance"
          value={fmtMoney(summary?.walletBalance)}
          info="Amount still to be paid out to the restaurant = Total Earnings − Total Settlements. A positive balance is money you owe the restaurant. A negative (red) balance means more was paid out than earned, so the restaurant owes it back."
          hint={
            (summary?.walletBalance || 0) > 0
              ? 'Pending payout to restaurant'
              : (summary?.walletBalance || 0) < 0
              ? 'Paid more than earned (excess payout)'
              : 'Fully settled'
          }
          accent={
            (summary?.walletBalance || 0) < 0
              ? 'bg-red-50 text-red-700'
              : 'bg-emerald-50 text-emerald-700'
          }
        />
        <StatCard
          icon={IndianRupee}
          label="Total Earnings"
          value={fmtMoney(summary?.restaurantEarning)}
          info="Everything the restaurant has earned so far: its share of every delivered order after the platform commission and fees are deducted, plus any manual earning entries. This is the restaurant's money, not the customers' order totals."
          hint="Restaurant share, after commission"
          accent="bg-orange-50 text-orange-700"
        />
        <StatCard
          icon={TrendingUp}
          label="Total Settlements"
          value={fmtMoney(summary?.totalSettlements)}
          info="Total amount already paid out to the restaurant, i.e. the sum of all settlements recorded with 'Record Settlement' below. Each payout reduces the Wallet Balance."
          hint="Already paid to restaurant"
          accent="bg-purple-50 text-purple-700"
        />
      </div>
      </TooltipProvider>

      {/* Settlement / Manual Transaction */}
      <div
        className="bg-white rounded-xl border border-gray-200 p-4"
        data-testid="restaurant-settle-block"
      >
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <p className="text-sm font-semibold text-gray-900">
              Record Settlement
            </p>
            <p className="text-xs text-gray-500">
              Mark a payout to the restaurant or log a manual earning entry.
              {summary && (
                <>
                  {' '}Current wallet balance:{' '}
                  <span
                    className={`font-semibold ${
                      (summary.walletBalance || 0) < 0
                        ? 'text-red-600'
                        : 'text-emerald-700'
                    }`}
                  >
                    {fmtMoney(summary.walletBalance)}
                  </span>
                  .
                </>
              )}
            </p>
          </div>
          <Button
            type="button"
            data-testid="restaurant-settle-toggle"
            onClick={() => setShowSettleForm((v) => !v)}
            style={{ backgroundColor: showSettleForm ? '#6B7280' : '#C2410C' }}
            className="text-white"
          >
            {showSettleForm ? (
              <>
                <X size={14} className="mr-1" /> Cancel
              </>
            ) : (
              <>
                <Plus size={14} className="mr-1" /> Record Settlement
              </>
            )}
          </Button>
        </div>

        {showSettleForm && (
          <form
            onSubmit={submitSettlement}
            className="mt-4 grid grid-cols-1 md:grid-cols-12 gap-2"
            data-testid="restaurant-settle-form"
          >
            <div className="md:col-span-2">
              <Label className="text-xs">Type</Label>
              <Select
                value={settle.type}
                onValueChange={(v) => setSettle({ ...settle, type: v })}
              >
                <SelectTrigger className="h-9" data-testid="settle-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="RESTAURANT_SETTLEMENT">
                    Settlement (payout)
                  </SelectItem>
                  <SelectItem value="RESTAURANT_EARNING">
                    Earning (adjustment)
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Amount (₹)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={settle.amount}
                onChange={(e) => setSettle({ ...settle, amount: e.target.value })}
                className="h-9"
                placeholder="0.00"
                data-testid="settle-amount"
                required
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Settlement date</Label>
              <Input
                type="date"
                value={settle.settlementDate}
                max={todayStr()}
                onChange={(e) => setSettle({ ...settle, settlementDate: e.target.value })}
                className="h-9"
                data-testid="settle-date"
                required
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Method</Label>
              <Select
                value={settle.paymentMethod}
                onValueChange={(v) => setSettle({ ...settle, paymentMethod: v })}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CASH">Cash</SelectItem>
                  <SelectItem value="UPI">UPI</SelectItem>
                  <SelectItem value="BANK_TRANSFER">Bank Transfer</SelectItem>
                  <SelectItem value="OTHER">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Note</Label>
              <Input
                value={settle.note}
                onChange={(e) => setSettle({ ...settle, note: e.target.value })}
                className="h-9"
                placeholder="Optional"
                data-testid="settle-note"
              />
            </div>
            <div className="md:col-span-2 flex items-end">
              <Button
                type="submit"
                disabled={saving}
                style={{ backgroundColor: '#C2410C' }}
                className="text-white w-full h-9"
                data-testid="settle-submit"
              >
                {saving ? 'Saving…' : 'Record'}
              </Button>
            </div>
          </form>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <Input
            data-testid="txn-search-input"
            placeholder="Search by order number / id..."
            className="pl-9 pr-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X size={16} />
            </button>
          )}
        </div>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]" data-testid="txn-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {statuses.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[220px]" data-testid="txn-type-filter">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {TYPE_OPTIONS.map((t) => (
              <SelectItem key={t} value={t}>
                {t.replace('RESTAURANT_', '').replace('_', ' ')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="ml-auto text-sm text-gray-600">
          Showing{' '}
          <span className="font-semibold text-gray-900">
            {filtered.length}
          </span>{' '}
          of {transactions.length}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="txn-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="p-3 text-left text-xs font-medium text-gray-600 uppercase">
                  Date
                </th>
                <th className="p-3 text-left text-xs font-medium text-gray-600 uppercase">
                  Order
                </th>
                <th className="p-3 text-left text-xs font-medium text-gray-600 uppercase">
                  Type
                </th>
                <th className="p-3 text-right text-xs font-medium text-gray-600 uppercase">
                  Amount
                </th>
                <th className="p-3 text-right text-xs font-medium text-gray-600 uppercase">
                  Earning
                </th>
                <th className="p-3 text-right text-xs font-medium text-gray-600 uppercase">
                  Platform
                </th>
                <th className="p-3 text-right text-xs font-medium text-gray-600 uppercase">
                  Delivery
                </th>
                <th className="p-3 text-left text-xs font-medium text-gray-600 uppercase">
                  Method
                </th>
                <th className="p-3 text-left text-xs font-medium text-gray-600 uppercase">
                  Note
                </th>
                <th className="p-3 text-left text-xs font-medium text-gray-600 uppercase">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filtered.map((t) => (
                <tr
                  key={t.id}
                  className="hover:bg-gray-50 transition-colors"
                  data-testid={`txn-row-${t.id}`}
                >
                  <td className="p-3 text-sm text-gray-700 whitespace-nowrap">
                    {fmtTxnDate(t)}
                  </td>
                  <td className="p-3">
                    {t.orderNumber ? (
                      <div className="font-medium text-gray-900">
                        {t.orderNumber}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-xs">—</span>
                    )}
                    {t.orderID && (
                      <div className="text-[10px] text-gray-400 font-mono truncate max-w-[160px]">
                        {t.orderID}
                      </div>
                    )}
                  </td>
                  <td className="p-3">
                    <Badge variant="secondary" className="text-[11px]">
                      {t.type || '-'}
                    </Badge>
                    {t.split && (
                      <div className="text-[10px] text-gray-400 mt-1">
                        {t.split}
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-right font-semibold tabular-nums">
                    {fmtMoney(t.amount)}
                  </td>
                  <td className="p-3 text-right text-green-700 tabular-nums">
                    {fmtMoney(t.restaurantEarning)}
                  </td>
                  <td className="p-3 text-right text-purple-700 tabular-nums">
                    {fmtMoney(t.platformFee)}
                  </td>
                  <td className="p-3 text-right text-gray-600 tabular-nums">
                    {fmtMoney(t.deliveryFee)}
                  </td>
                  <td className="p-3">
                    <Badge
                      variant="outline"
                      className="text-[11px] font-normal"
                    >
                      {t.paymentMethod || '-'}
                    </Badge>
                  </td>
                  <td className="p-3 text-xs text-gray-600 max-w-[180px] truncate" title={t.note || ''}>
                    {t.note || <span className="text-gray-400">-</span>}
                  </td>
                  <td className="p-3">
                    <Badge className={`${statusColor(t.status)} border-0`}>
                      {t.status || '-'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filtered.length === 0 && (
          <p
            className="text-center text-gray-500 py-12"
            data-testid="txn-empty"
          >
            {transactions.length === 0
              ? 'No transactions yet for this restaurant.'
              : 'No transactions match your filters.'}
          </p>
        )}
      </div>
    </div>
  );
};

export default TransactionsTab;
